import React, { useState, useEffect } from "react";
import axios from "axios";
import { Check, Loader2, AlertCircle, Plus, Edit, Trash2 } from "lucide-react";
import CreatePackageModal from "../../components/AdminComponents/package/CreatePackageModal";
import EditPackageModal from "../../components/AdminComponents/package/EditPackageModal";
import PackageSkeletonBody from "../../components/AdminComponents/package/PackageSkeletonBody";
import Swal from "sweetalert2";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

const Packages = () => {
  const [packages, setPackages] = useState([]);
  const [filteredPackages, setFilteredPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/packages/get-all-package`,
        // { withCredentials: true }
      );

      if (response.data.success) {
        setPackages(response.data.data);
        setFilteredPackages(response.data.data);
      }
    } catch (err) {
      console.error("Error fetching packages:", err);
      setError(err.response?.data?.message || "Failed to load packages");
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePackage = () => {
    setShowCreateModal(true);
  };

  const handleEditPackage = (pkg) => {
    console.log("Edit package:", pkg._id);
    setShowEditModal(true);
    setSelectedPackage(pkg);
  };

  const handleDeletePackage = async (pkg) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This inquiry will be permanently deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#0bb6d9",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      setDeleteLoading(pkg._id);

      await axios.delete(
        `${process.env.REACT_APP_API_URL}/packages/delete-package/${pkg._id}`,
        // { withCredentials: true }
      );

      const updatedPackages = packages.filter((p) => p._id !== pkg._id);
      setPackages(updatedPackages);
      setFilteredPackages(updatedPackages);

      await Swal.fire({
        title: "Deleted!",
        text: "Package deleted successfully.",
        icon: "success",
        confirmButtonColor: "#0bb6d9",
      });
    } catch (err) {
      console.error("Error deleting package:", err);
      Swal.fire({
        title: "Error!",
        text: err.response?.data?.message || "Failed to delete package.",
        icon: "error",
        confirmButtonColor: "#d33",
      });
    } finally {
      setDeleteLoading(null);
    }
  };

  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentPackages = filteredPackages.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredPackages.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const getPageNumbers = () => {
    const pages = [];

    if (totalPages <= 5) {
      // Show all pages if 5 or fewer
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage > 3) {
        pages.push("...");
      }

      // Show pages around current page
      const startPage = Math.max(2, currentPage - 1);
      const endPage = Math.min(totalPages - 1, currentPage + 1);

      for (let i = startPage; i <= endPage; i++) {
        if (!pages.includes(i)) {
          pages.push(i);
        }
      }

      if (currentPage < totalPages - 2) {
        pages.push("...");
      }

      // Always show last page
      pages.push(totalPages);
    }

    return pages;
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-800 mb-2">
            Error Loading Packages
          </h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={fetchPackages}
            className="bg-[#0bb6d9] text-white px-6 py-2 rounded-lg hover:bg-[#069ab7] transition"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-center mb-10 gap-4">
          <div className="text-center md:text-left">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
              <span className="text-[#0bb6d9]">Packages</span>
            </h1>
            <p className="text-gray-600">
              Showing {currentPackages.length} of {filteredPackages.length} packages
            </p>
          </div>

          <button
            onClick={handleCreatePackage}
            className="flex items-center gap-2 bg-[#0bb6d9] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#069ab7] transition-colors duration-300 shadow-lg hover:shadow-xl"
          >
            <Plus className="w-5 h-5" />
            Create Package
          </button>
        </div>

        {loading ? (
          <PackageSkeletonBody />
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {currentPackages.length > 0 ? (
                currentPackages.map((pkg) => (
                  <div
                    key={pkg._id}
                    className="bg-white rounded-2xl shadow-xl overflow-hidden transform transition-all duration-300 hover:shadow-2xl"
                  >
                    {pkg.tag && (
                      <div className="bg-gradient-to-r from-[#0bb6d9] to-[#3cd7f6] text-white text-center md:py-3 py-2 ">
                        <span className="text-sm font-semibold uppercase tracking-wide">
                          {pkg.tag}
                        </span>
                      </div>
                    )}

                    <div className="md:p-8 p-4">
                      <h3 className="text-2xl font-bold text-gray-800 mb-2">
                        {pkg.priceTitle}
                      </h3>

                      <p className="text-gray-600 text-sm mb-6">
                        {pkg.priceDescription}
                      </p>

                      <div className="mb-6">
                        <div className="flex md:flex-row flex-col items-baseline">
                          <span className="text-3xl font-bold text-[#0bb6d9]">
                            ₹{pkg.price}
                          </span>
                          <span className="text-gray-500 ml-2 text-sm">
                            {pkg.priceDetail}
                          </span>
                        </div>
                      </div>

                      <div className="space-y-3 mb-8">
                        {pkg.features && pkg.features.length > 0 ? (
                          pkg.features.map((feature, index) => (
                            <div key={index} className="flex items-start">
                              <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                              <span className="text-gray-700 text-sm">
                                {feature}
                              </span>
                            </div>
                          ))
                        ) : (
                          <p className="text-gray-500 text-sm italic">
                            No features listed
                          </p>
                        )}
                      </div>

                      <div className="flex gap-3">
                        <button
                          onClick={() => handleEditPackage(pkg)}
                          className="flex-1 flex items-center justify-center gap-2 bg-[#0bb6d9]  hover:bg-[#069ab7]  text-white py-3 rounded-lg font-semibold  transition-colors duration-300 shadow-md hover:shadow-lg"
                        >
                          <Edit className="w-4 h-4" />
                          Edit
                        </button>

                        <button
                          onClick={() => handleDeletePackage(pkg)}
                          disabled={deleteLoading === pkg._id}
                          className="flex-1 flex items-center justify-center gap-2 bg-red-500 text-white py-3 rounded-lg font-semibold hover:bg-red-600 transition-colors duration-300 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {deleteLoading === pkg._id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-full text-center py-12">
                  <p className="text-gray-500 text-lg">No packages found.</p>
                </div>
              )}
            </div>
          </>
        )}

        {totalPages > 1 && (
          <div className="flex justify-end items-center mt-8 space-x-2">
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              <FaChevronLeft size={14} /> Prev
            </button>

            {getPageNumbers().map((page, index) => (
              <div key={index}>
                {page === "..." ? (
                  <span className="px-3 py-2 text-gray-500">...</span>
                ) : (
                  <button
                    onClick={() => handlePageChange(page)}
                    className={`px-3 py-2 rounded-md transition-colors ${currentPage === page
                      ? "bg-blue-500 text-white"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      }`}
                  >
                    {page}
                  </button>
                )}
              </div>
            ))}

            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              Next <FaChevronRight size={14} />
            </button>
          </div>
        )}

        {showCreateModal && (
          <CreatePackageModal
            onClose={() => setShowCreateModal(false)}
            fetchPackages={fetchPackages}
          />
        )}
        {showEditModal && (
          <EditPackageModal
            packageData={selectedPackage}
            fetchPackages={fetchPackages}
            onClose={() => setShowEditModal(false)}
          />
        )}
      </div>
    </div>
  );
};

export default Packages;